import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  addOrUpdateCart,
  doCartFlush,
  fetchCartList,
  removeItemFromCart,
  orderForSomeOneElse,
  removeSomeOneElse,
} from "./CartApi";
import { CartState } from "../../../features/view/public/shopping/cart/CartTypes";

const initialState: CartState = {
  addTocart: {
    data: [],
    loader: false,
    billingData: {
      subtotal: 0.0,
      taxes_and_charges: 0.0,
      delivery_fee: 0.0,
      grand_total: 0.0,
      total_savings: 0.0,
      coupon_code: "",
      discount: 0.0,
    },
    available_payment_modes: [],
    some_one_else_order: {
      name: "",
      phone_number: "",
    },
    additional_info: "",
    phone_no: "",
    delivery_discount_message: "",
    customization_items: [],
    long_distance: false,
    long_distance_charges: {
      delivery_fee_online_express: 0,
      delivery_fee_online_nxt: 0,
      delivery_fee_online_surface: 0,
      delivery_fee_cod_express: 0,
      delivery_fee_cod_nxt: 0,
      delivery_fee_cod_surface: 0,
      expected_tat_surface: {
        tat: null,
        expected_delivery_date: null,
      },
      expected_tat_express: {
        tat: null,
        expected_delivery_date: null,
      },
      expected_tat_next_day: {
        tat: null,
        expected_delivery_date: null,
      },
    },
  },
};

export const fetchCartListAsync = createAsyncThunk(
  "cart/get/list",
  async (id: string | null) => {
    const response = await fetchCartList(id);
    return response;
  },
);

export const addOrUpdateCartAsync = createAsyncThunk(
  "cart/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await addOrUpdateCart(payload);
      return response;
    } catch (error: any) {
      // axios interceptor rejects with error.response — keep it on payload
      return rejectWithValue(error);
    }
  },
);

export const removeItemFromCartAsync = createAsyncThunk(
  "cart/delete",
  async (payload: any) => {
    const response = await removeItemFromCart(payload);
    return response;
  },
);
export const cartFlushAsync = createAsyncThunk(
  "cart/flush",
  async (payload: any) => {
    const response = await doCartFlush(payload);
    return response;
  },
);

export const orderForSomeOneElseAsync = createAsyncThunk(
  "orders/add-or-update-someone-else-detail",
  async (payload: any) => {
    const response = await orderForSomeOneElse(payload);
    return response;
  },
);

export const removeSomeOneElseAsync = createAsyncThunk(
  "orders/delete-order-for-someone-else-detail",
  async () => {
    const response = await removeSomeOneElse();
    return response;
  },
);
export const CartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setAdditionalInfo: (
      state,
      action: PayloadAction<{ additional_info: string; phone_no: string }>,
    ) => {
      state.addTocart.additional_info = action.payload?.additional_info || "";
      state.addTocart.phone_no = action.payload?.phone_no || "";
    },
    setSomeOneElseOrder: (
      state,
      action: PayloadAction<{
        name: string;
        phone_number: string;
        phone_no?: string;
      }>,
    ) => {
      state.addTocart.some_one_else_order = {
        name: action.payload.name || "",
        phone_number: action.payload.phone_number || "",
      };
      if (action.payload.phone_no !== undefined) {
        state.addTocart.some_one_else_order.phone_no = action.payload.phone_no;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCartListAsync.pending, (state) => {
        state.addTocart.loader = true;
      })
      .addCase(fetchCartListAsync.fulfilled, (state, action: any) => {
        state.addTocart.loader = false;
        state.addTocart.data = action?.payload?.data?.data || [];
        state.addTocart.available_payment_modes =
          action?.payload?.data?.available_payment_modes || [];
        state.addTocart.billingData = action?.payload?.data?.price_data || {};
        const someoneElseOrder = action?.payload?.data?.some_one_else_order;
        if (someoneElseOrder?.name || someoneElseOrder?.phone_number) {
          state.addTocart.some_one_else_order = someoneElseOrder;
        }
        state.addTocart.delivery_discount_message =
          action?.payload?.data?.delivery_discount_message || "";
        state.addTocart.customization_items =
          action?.payload?.data?.customization_items || [];
        state.addTocart.long_distance =
          action?.payload?.data?.long_distance || false;
        state.addTocart.long_distance_charges =
          action?.payload?.data?.long_distance_charges ||
          state.addTocart.long_distance_charges;
      })
      .addCase(fetchCartListAsync.rejected, (state) => {
        state.addTocart.loader = false;
      })
      .addCase(addOrUpdateCartAsync.pending, (state) => {
        state.addTocart.loader = true;
      })
      .addCase(addOrUpdateCartAsync.fulfilled, (state, action: any) => {
        state.addTocart.loader = false;
        state.addTocart.data = action?.payload?.data?.data || [];
        state.addTocart.billingData = action?.payload?.data?.price_data || {};
        state.addTocart.delivery_discount_message =
          action?.payload?.data?.delivery_discount_message || "";
        state.addTocart.customization_items =
          action?.payload?.data?.customization_items || [];
        state.addTocart.long_distance =
          action?.payload?.data?.long_distance || false;
        state.addTocart.long_distance_charges =
          action?.payload?.data?.long_distance_charges ||
          state.addTocart.long_distance_charges;
      })
      .addCase(addOrUpdateCartAsync.rejected, (state) => {
        state.addTocart.loader = false;
      })
      .addCase(orderForSomeOneElseAsync.fulfilled, (state, action: any) => {
        const savedOrder = action?.payload?.data?.some_one_else_order;
        const submittedOrder = action.meta.arg || {};
        state.addTocart.some_one_else_order = {
          name: savedOrder?.name || submittedOrder.name || "",
          phone_number:
            savedOrder?.phone_number || submittedOrder.phone_number || "",
        };
      })
      .addCase(removeSomeOneElseAsync.fulfilled, (state) => {
        state.addTocart.some_one_else_order = {
          name: "",
          phone_number: "",
        };
      });
  },
});

export const { setAdditionalInfo, setSomeOneElseOrder } = CartSlice.actions;
export default CartSlice.reducer;
